import { useEffect } from 'react';

/**
 * Per-route SEO for a client-rendered app: keeps title, description,
 * canonical, Open Graph/Twitter tags and JSON-LD in sync with the route.
 */

const SITE_URL = 'https://bulgebracket.co';
const LD_ATTR = 'data-page-ld';

export interface PageMeta {
  title: string;
  description: string;
  /** Route path beginning with '/', used for canonical + og:url. */
  path: string;
  /** Page-level structured data (Organization LD stays in index.html). */
  jsonLd?: object[];
}

const setMeta = (attr: 'name' | 'property', key: string, content: string): void => {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const usePageMeta = ({ title, description, path, jsonLd }: PageMeta): void => {
  useEffect(() => {
    const url = `${SITE_URL}${path === '/' ? '/' : path}`;

    document.title = title;
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = url;

    document.head.querySelectorAll(`script[${LD_ATTR}]`).forEach(node => node.remove());
    (jsonLd ?? []).forEach(data => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute(LD_ATTR, 'true');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });
  }, [title, description, path, jsonLd]);
};

export default usePageMeta;
